// src/env.ts
// Client-side environment validation — Zod schema for VITE_* vars.
// All client-side env access goes through this module.

import { z } from 'zod'

const envSchema = z.object({
  /** Supabase project URL */
  VITE_SUPABASE_URL: z.string().url(),
  /** Supabase anon (public) key — safe to expose, RLS enforces access */
  VITE_SUPABASE_ANON_KEY: z.string().min(1),
  /** Public URL of the app (e.g. https://sitegenius.net) */
  VITE_APP_URL: z.string().url().optional(),
  /** Bridge server URL (e.g. https://bridge.sitegenius.net) */
  VITE_BRIDGE_URL: z.string().url().optional(),
  /** Bridge server origin for PostMessage validation (e.g. https://bridge.sitegenius.net) */
  VITE_BRIDGE_ORIGIN: z.string().url().optional(),
})

export type ClientEnv = z.infer<typeof envSchema>

function loadClientEnv(): ClientEnv {
  const result = envSchema.safeParse({
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
    VITE_APP_URL: import.meta.env.VITE_APP_URL,
    VITE_BRIDGE_URL: import.meta.env.VITE_BRIDGE_URL,
    VITE_BRIDGE_ORIGIN: import.meta.env.VITE_BRIDGE_ORIGIN,
  })

  if (!result.success) {
    const formatted = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n')

    if (import.meta.env.PROD) {
      throw new Error(
        `Client environment validation failed:\n${formatted}`,
      )
    }

    console.warn(
      `[env] Client environment validation warnings:\n${formatted}`,
    )

    // Return best-effort in dev
    return {
      VITE_SUPABASE_URL:
        import.meta.env.VITE_SUPABASE_URL ?? 'http://localhost:54321',
      VITE_SUPABASE_ANON_KEY:
        import.meta.env.VITE_SUPABASE_ANON_KEY ?? 'dev-anon-key',
      VITE_APP_URL: import.meta.env.VITE_APP_URL,
      VITE_BRIDGE_URL: import.meta.env.VITE_BRIDGE_URL,
      VITE_BRIDGE_ORIGIN: import.meta.env.VITE_BRIDGE_ORIGIN,
    }
  }

  return result.data
}

/** Validated client-side environment variables. */
export const clientEnv = loadClientEnv()
